// API utility for making authenticated requests
import { API_BASE_URL } from '../config';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

class ApiClient {
  private getAuthHeaders(): HeadersInit {
    // COMMENTED OUT: Token-based authentication is disabled
    // const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      // ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    if (response.ok) {
      try {
        const data = await response.json();
        return { success: true, data };
      } catch (error) {
        return { success: true, data: {} as T };
      }
    } else {
      let errorMessage = 'Request failed';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      return { success: false, error: errorMessage };
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
        credentials: 'include', // Include cookies for session
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        credentials: 'include', // Include cookies for session
        body: data ? JSON.stringify(data) : undefined,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        credentials: 'include', // Include cookies for session
        body: data ? JSON.stringify(data) : undefined,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
        credentials: 'include', // Include cookies for session
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  async upload<T>(endpoint: string, file: File, type: string): Promise<ApiResponse<T>> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}${endpoint}/${type}`, {
        method: 'POST',
        credentials: 'include', // Include cookies for session
        body: formData,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }
}

export const apiClient = new ApiClient();

// COMMENTED OUT: Authentication-specific API functions are disabled
// Authentication-specific API functions
// export const authApi = {
//   login: async (username: string, password: string) => {
//     return apiClient.post('/api/auth/login', { username, password });
//   },

//   verify: async () => {
//     return apiClient.get('/api/auth/verify');
//   },

//   logout: async () => {
//     return apiClient.post('/api/auth/logout');
//   },

//   requestAccess: async (data: {
//     name: string;
//     email: string;
//     phone: string;
//     poste: string;
//     direction: string;
//   }) => {
//     return apiClient.post('/api/auth/request-access', data);
//   }
// };

// File upload API functions
export const uploadApi = {
  uploadShapefile: (file: File) => {
    return apiClient.upload('/api/upload', file, 'shapefile');
  },

  uploadCSV: (file: File) => {
    return apiClient.upload('/api/upload', file, 'csv');
  }
};

// Edit Logs API
export const logsApi = {
  getEditLogs: async (params: { limit?: number; offset?: number; type?: string; featureId?: string; search?: string }) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.type) queryParams.append('type', params.type);
    if (params.featureId) queryParams.append('featureId', params.featureId);
    if (params.search) queryParams.append('search', params.search);

    return apiClient.get<any>(`/api/edit-logs?${queryParams.toString()}`);
  }
}; 